/**
 * Player-facing copy ported from Unity's GameConstants.cs so the React cashier
 * and identity-check surface display the exact same strings Unity does.
 *
 * Source of truth: apps/unity/Assets/Scripts/Constants/GameConstants.cs.
 * Keep these in lockstep — when Unity changes its copy, mirror the change here.
 *
 * Format placeholders mirror Unity's `string.Format`:
 *   `${0}`  → first arg, `${1}` → second arg, etc.
 * Use `formatUnityString(template, ...args)` to interpolate.
 */

// ── Titles ──────────────────────────────────────────────────────────────────
export const CASHIER_LOCKED_TITLE = 'Cashier Locked';
export const LOADING_TITLE = 'Loading';
export const CRYPTO_WITHDRAW_TITLE = 'Crypto Withdrawal';
export const INPERSON_WITHDRAW_TITLE = 'In-Person Withdrawal';
export const TRANSFER_TITLE = 'Player Transfer';
export const INPERSON_DEPOSIT_TITLE = 'In-Person Deposit';
export const BITCOIN_DEPOSIT_TITLE = 'Bitcoin Deposit';
export const CARD_DEPOSIT_TITLE = 'Credit Card Deposit';

// ── Identity check / device recognition ─────────────────────────────────────
export const IDENTITY_PROCESS_CHECKING = 'Identity Process Checking...';
export const NO_SESSION_ID_MESSAGE =
  'There appears to be an issue verifying your device. Please contact support for assistance.';
export const IS_PENDING_VERIFICATION_MESSAGE =
  "The user account has ongoing verification. To perform transactions such as withdrawals or transfers, please check the status. Navigate to the 'Accounts' section and click on check status.";
export const NO_VERIFICATION_MESSAGE =
  'The user account is currently not verified. To perform transactions such as withdrawals or transfers, Please verify your ID. Navigate back to login to initiate the re-verification process. ';
export const UNVERIFIED_MESSAGE =
  "Howdy Partner, before ya can mosey on outta here with yer loot, we gotta make sure you are who ya say ya are. So, finish up that ID check, and then yer gold is good to go.";

// KycQuizView prefab copy (apps/unity/Assets/Prefabs/KycQuizView.prefab)
export const IDENTITY_CHECK_TITLE = 'Identity Check';
export const IDENTITY_CHECK_FAILED_MESSAGE =
  "You've failed the identity check. Please reach out to support.";

// ── Withdrawal / deposit limits ─────────────────────────────────────────────
export const WITHDRAW_MINIMUM = 'The Minimum Withdrawal amount is $10.00.';
export const CRYPTO_MINIMUM_MESSAGE = 'The Minimum Crypto Withdrawal amount is $20.00';
/** Format: `EXCEEDED_MAXIMUM_WITHDRAW` takes `{0} = max daily limit`. */
export const EXCEEDED_MAXIMUM_WITHDRAW =
  'The Minimum Withdrawal amount is $10.00 and Maximum Transaction amount per day is ${0}.';
/** Format: `EXCEEDED_MAXIMUM_AMOUNT_ALLOWED` takes `{0} = max, {1} = current total`. */
export const EXCEEDED_MAXIMUM_AMOUNT_ALLOWED =
  'You have exceeded the maximum transaction allowed ${0}. Your total transaction as of the moment ${1}.';
/** Format: `EXCEEDED_MAXIMUM_TRANSFER` takes `{0} = max daily transfer`. */
export const EXCEEDED_MAXIMUM_TRANSFER =
  'You have exceeded the maximum allowed daily send limit of ${0}.';

// ── Cage / club ─────────────────────────────────────────────────────────────
export const CLUB_NOT_SELECTED = 'You have not selected a Club.';

// ── Address / wallet ────────────────────────────────────────────────────────
export const ADDRESS_REQUIRED_TRANSACTION_MESSAGE =
  'Please update your mailing address before proceeding with the transaction';
// Button copy from Unity's address-required modal in CashiersMenuView. The
// CTA navigates the player to ProfilePanel with the streetAddress field
// focused, mirroring AnimatedSwapPushLeft(AccountSettingsView).
export const GO_TO_ACCOUNT_SETTINGS_BUTTON = 'Go to Account Profile';
export const WITHDRAWAL_WALLET_ADDRESS_EMPTY_MESSAGE1 =
  "Howdy, Partner! To ante up and use this feature, you'll need to add a Bitcoin withdrawal address to your profile. Click here to mosey on over to your profile and get it set up.";
export const WITHDRAWAL_WALLET_ADDRESS_EMPTY_MESSAGE2 =
  'Bitcoin withdrawal address is empty. Please enter a valid address.';

// ── Generic error / connectivity ────────────────────────────────────────────
export const ERROR_MESSAGE =
  'An unexpected error occurred.\nTry again later or contact customer support.';
export const PROTOCOL_ERROR_MESSAGE =
  "We're having trouble processing your request. If this continues, please contact support.";
export const DATA_PROCESSING_ERROR_MESSAGE =
  "The server returned data we couldn't process. Please try again or contact support if the problem continues.";
export const CONNECTION_ERROR_MESSAGE =
  "We couldn't connect to the server. Please check your internet connection and try again.";
export const SERVER_TIMEOUT_MESSAGE =
  "Hmm... It's taking longer than expected. Please check your internet connection and try again.";

// ── Minimal {0}/{1} formatter (mirrors C# string.Format for the placeholders we use) ──
// The literal `$` in Unity templates (e.g. "${0}") is preserved; only `{N}` is substituted.
export function formatUnityString(template: string, ...args: (string | number)[]): string {
  return template.replace(/\{(\d+)\}/g, (match, idx) => {
    const i = Number(idx);
    if (!Number.isFinite(i) || i < 0 || i >= args.length) return match;
    return String(args[i]);
  });
}
